import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';

const CreateOnboardingVideo = ({ show, onHide }) => {
    const [formData, setFormData] = useState({
        seq_no: '',
        app_version: '',
        video_url: '',
        thumbnail_url: '',
        duration_sec: '',
        title: '',
        title_hin: '',
        title_mar: '',
        title_guj: '',
        status: 1,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const resetForm = () => {
        setFormData({
            seq_no: '',
            app_version: '',
            video_url: '',
            thumbnail_url: '',
            duration_sec: '',
            title: '',
            title_hin: '',
            title_mar: '',
            title_guj: '',
            status: 1,
        });
        setIsSubmitted(false);
    };

    const handleClose = () => {
        resetForm();
        onHide();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        if (!formData.seq_no || isNaN(Number(formData.seq_no)) || Number(formData.seq_no) < 1) {
            toast.error('Sequence number must be a positive integer');
            return false;
        }
        if (!formData.video_url.trim()) {
            toast.error('Video URL is required');
            return false;
        }
        try {
            new URL(formData.video_url);
        } catch {
            toast.error('Please enter a valid Video URL');
            return false;
        }
        if (formData.thumbnail_url.trim()) {
            try {
                new URL(formData.thumbnail_url);
            } catch {
                toast.error('Please enter a valid Thumbnail URL');
                return false;
            }
        }
        if (formData.duration_sec && (isNaN(Number(formData.duration_sec)) || Number(formData.duration_sec) < 0)) {
            toast.error('Duration must be a non-negative number');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            setIsSubmitting(true);

            const payload = {
                ...formData,
                seq_no: Number(formData.seq_no),
                duration_sec: formData.duration_sec ? Number(formData.duration_sec) : null,
                app_version: formData.app_version.trim() || null,
                status: Number(formData.status),
            };

            const response = await _axios.post(`${ServerPath}/onboarding-videos/admin`, payload);

            if (response.data.success) {
                toast.success('Onboarding video created successfully');
                setIsSubmitted(true);
                handleClose();
            } else {
                toast.error(response.data.message || 'Failed to create video');
            }
        } catch (error) {
            console.error('[CreateOnboardingVideo] error:', error);
            toast.error(error.response?.data?.message || 'Failed to create video');
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputStyle = {
        height: '46px',
        border: '1px solid #E2E8F0',
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
    };

    const labelStyle = {
        color: '#4A5568',
        fontWeight: '500',
        fontSize: '15px',
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
            <div style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <Modal.Header
                    closeButton
                    style={{ backgroundColor: '#F7F8FC', borderBottom: '1px solid #E2E8F0', padding: '16px 24px' }}
                >
                    <Modal.Title style={{ fontWeight: '600', fontSize: '18px', color: '#2D3748' }}>
                        Add Onboarding Video
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '24px', backgroundColor: '#FFFFFF' }}>
                    <Form>
                        {/* Row 1 — seq_no / app_version / duration / status */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Seq No <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="seq_no"
                                        value={formData.seq_no}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 1"
                                        min="1"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        App Version
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="app_version"
                                        value={formData.app_version}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 2.7.0 (blank = all)"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Duration (sec)
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="duration_sec"
                                        value={formData.duration_sec}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 30"
                                        min="0"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Status <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        style={inputStyle}
                                    >
                                        <option value={1}>Active</option>
                                        <option value={0}>Inactive</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Row 2 — video_url */}
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Video URL <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        name="video_url"
                                        value={formData.video_url}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com/video.mp4"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Row 3 — thumbnail_url */}
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>
                                        Thumbnail URL
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        name="thumbnail_url"
                                        value={formData.thumbnail_url}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com/thumb.jpg"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Divider */}
                        <hr style={{ borderColor: '#E2E8F0', margin: '8px 0 20px' }} />
                        <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px' }}>
                            Titles — leave blank for languages not supported
                        </p>

                        {/* Row 4 — titles */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>Title (English)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter English title"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>Title (Hindi)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title_hin"
                                        value={formData.title_hin}
                                        onChange={handleInputChange}
                                        placeholder="हिंदी शीर्षक दर्ज करें"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>Title (Marathi)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title_mar"
                                        value={formData.title_mar}
                                        onChange={handleInputChange}
                                        placeholder="मराठी शीर्षक प्रविष्ट करा"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={labelStyle}>Title (Gujarati)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title_guj"
                                        value={formData.title_guj}
                                        onChange={handleInputChange}
                                        placeholder="ગુજરાતી શીર્ષક દાખલ કરો"
                                        style={inputStyle}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>

                <div style={{ padding: '0 24px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        style={{
                            borderRadius: '6px',
                            padding: '10px 30px',
                            fontWeight: '500',
                            fontSize: '15px',
                            marginRight: '12px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isSubmitted}
                        style={{
                            backgroundColor: '#5662F6',
                            borderColor: '#5662F6',
                            borderRadius: '6px',
                            padding: '10px 40px',
                            fontWeight: '500',
                            fontSize: '15px',
                        }}
                    >
                        {isSubmitting ? 'Creating...' : 'Create'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateOnboardingVideo;
